import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Input, RegularModal, TextArea } from '@/components_deprecated'
import type { UserEquipmentTypeForSettings } from '..';
import { Field, Form } from '..'

export interface UserEquipmentTypeForm {
  name: string
  description: string
}

interface UserEquipmentTypePromptProps {
  initialData?: UserEquipmentTypeForm
  editableTypes: UserEquipmentTypeForSettings[]

  onSubmit: PromptResolve<UserEquipmentTypeForm | null>
}

export const UserEquipmentTypePrompt: React.FC<
  UserEquipmentTypePromptProps
> = ({ initialData, editableTypes, onSubmit }) => {
  const translations = useTranslate({
    createEquipmentType: 'settings.create-equipment-type',
    editEquipmentType: 'settings.edit-equipment-type',
    name: 'common.name',
    description: 'common.description',
    cancel: 'common.cancel',
    save: 'common.save',

    validation: {
      mustHaveName: 'settings.validation.equipment-type.must-have-name',
      nameExists: 'settings.validation.equipment-type.name-exists',
    },
  })

  const editing = typeof initialData !== 'undefined'

  const {
    formValues: form,
    formValid,
    formErrors: errors,
    formChangeHandler: handler,
  } = useForm<UserEquipmentTypeForm>({
    values: {
      name: initialData?.name ?? '',
      description: initialData?.description ?? '',
    },
    validators: {
      name: (val: string) => {
        if (!val) return translations.validation.mustHaveName
        if (
          editableTypes.some(
            type =>
              type.name === val && (!editing || type.name !== initialData?.name)
          )
        ) {
          return translations.validation.nameExists
        }
        return null
      },
    },
  })

  return (
    <RegularModal
      title={
        editing
          ? `${translations.editEquipmentType}: ${initialData?.name ?? ''}`
          : translations.createEquipmentType
      }
      width="480px"
      submitDisabled={!formValid}
      submitText={translations.save}
      onCancel={() => onSubmit(null)}
      onSubmit={() => onSubmit(form)}
    >
      <Form>
        <Field>
          <label>{translations.name}</label>
          <Input
            value={form.name}
            error={errors.name}
            fullWidth
            autoFocus
            onChange={handler('name')}
          />
        </Field>

        <Field>
          <label>{translations.description}</label>
          <TextArea
            value={form.description}
            fullWidth
            rows={3}
            onChange={handler('description')}
          />
        </Field>
      </Form>
    </RegularModal>
  )
}
